import { Controller } from "@hotwired/stimulus"

// Keeps a one-time-code field digits-only and submits the form as soon as the
// code is complete (typed, pasted, or filled by the OS from an SMS/mail).
//
// A paste is normalised before the browser touches the field, so "482 913",
// "482-913" or a whole email containing the code all land as six digits.
//
// The form posts once. The auto-submit on the sixth digit is usually followed
// by Enter or a tap on Verify; that second POST either lands after the first
// signed the learner in (rotated session → 422 InvalidAuthenticityToken page)
// or aborts the first after the server already spent the code ("code
// expired", not signed in). A resubmit is allowed again after RESUBMIT_AFTER_MS
// in case the first request hangs, and when the page comes back from the
// back/forward cache.
const RESUBMIT_AFTER_MS = 10000

export default class extends Controller {
  static targets = ["input"]
  static values = { length: { type: Number, default: 6 } }

  connect() {
    this.submitted = false
    this.submittedAt = null
  }

  submit(event) {
    if (this.submittedAt && Date.now() - this.submittedAt < RESUBMIT_AFTER_MS) {
      event.preventDefault()
      return
    }
    this.submittedAt = Date.now()
  }

  pageshow(event) {
    if (event.persisted) this.submittedAt = null
  }

  paste(event) {
    const text = event.clipboardData?.getData("text")
    if (!text) return

    event.preventDefault()
    this.inputTarget.value = this.extractCode(text)
    this.format()
  }

  format() {
    const input = this.inputTarget
    const digits = input.value.replace(/\D/g, "").slice(0, this.lengthValue)
    if (input.value !== digits) input.value = digits

    if (digits.length < this.lengthValue) {
      this.submitted = false
    } else if (!this.submitted) {
      this.submitted = true
      input.form.requestSubmit()
    }
  }

  // The first run of `length` digits, allowing a single space, no-break space
  // or dash between digits (how mail clients and the email itself group them);
  // otherwise whatever digits the text contains.
  extractCode(text) {
    const run = new RegExp(`\\d(?:[\\s\\u00a0-]?\\d){${this.lengthValue - 1}}`)
    const match = text.match(run)
    const source = match ? match[0] : text
    return source.replace(/\D/g, "").slice(0, this.lengthValue)
  }
}
